import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect
} from 'react';
import { useHistory } from 'react-router-dom';
import './index.scss';

import PropTypes from 'prop-types';
import {
  WalletIcon,
  ChevronRightIcon
} from '../../../assets/images/bk-wallet/icons';

import WalletHeader from '../../components/wallet/wall-header';
import WalletBalanceCard from '../../components/wallet/wallet-balance-card';
import WalletSectionHeader from '../../components/wallet/wallet-section-header';
import WalletTransactionList from '../../components/wallet/wall-transaction-list';
import WalletTabs from '../../components/wallet/wallet-tabs';
import WalletTransaction from '../../components/wallet/wallet-transaction';
import {
  useWalletSummarySelector,
  useWalletTransactions,
  useWalletSummaryDispatch
} from '../../hooks/useWallet';

import useViewport from '../../hooks/useViewport';

import Loader from '../../components/loader-spin';
import { formatDateTime } from '../../utils/services';

const tabs = [
  {
    label: 'All',
    value: 'all'
  },
  {
    label: 'Credited',
    value: 'credit'
  },
  {
    label: 'Debited',
    value: 'debit'
  }
];

const BkWalletWithTabs = ({ onNavigate, theme }) => {
  const {
    transactions,
    handleTabChange,
    filters,
    isWalletTransactionsLoading,
    loadingMoreRef,
    resetTransactionsHistory,
    hasNextPage
  } = useWalletTransactions();

  const handleGoBack = useCallback(() => {
    if (typeof onNavigate === 'function') {
      onNavigate('without-tabs');
    }

    if (typeof resetTransactionsHistory === 'function') {
      resetTransactionsHistory();
    }
  }, [onNavigate]);

  const walletContent = () => {
    return (
      <>
        <WalletTransactionList
          transactions={transactions}
          className="wallet__transparent-transactions-bg"
          loading={isWalletTransactionsLoading && !hasNextPage}
          renderItem={transaction => (
            <WalletTransaction
              title={transaction?.description}
              date={formatDateTime(transaction?.createdAt)}
              amount={transaction?.amount}
              type={(transaction?.transactionType || '').toLowerCase()}
              showIcon={false}
            />
          )}
        />
      </>
    );
  };

  return (
    <div className="wallet" data-tabs="true" data-theme={theme}>
      <WalletHeader
        title="All Transactions"
        showBackButton
        onBack={handleGoBack}
      />
      <WalletTabs
        tabs={tabs}
        activeTab={filters.type}
        onChange={handleTabChange}
      />
      {walletContent()}
      {hasNextPage && (
        <div ref={loadingMoreRef}>
          <Loader />
        </div>
      )}
    </div>
  );
};

BkWalletWithTabs.propTypes = {
  onNavigate: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired
};

const BKWalletWithoutTabs = ({ onNavigate }) => {
  const history = useHistory();
  const { isMobile: isMobileScreen } = useViewport();
  const {
    recentTransactions,
    walletAmount,
    isWalletSummaryLoading
  } = useWalletSummarySelector();
  const { getWalletSummary } = useWalletSummaryDispatch();

  const handleGoToViewAll = useCallback(() => {
    if (typeof onNavigate === 'function') onNavigate('tabs');
  }, [onNavigate]);

  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  useEffect(() => {
    getWalletSummary();
  }, [getWalletSummary]);

  const walletContent = () => {
    if (isWalletSummaryLoading) {
      return <Loader />;
    }

    return (
      <>
        <WalletBalanceCard>
          <WalletBalanceCard.Content>
            <WalletBalanceCard.Content.Title>
              Your Balance
            </WalletBalanceCard.Content.Title>
            <WalletBalanceCard.Content.Amount className="wallet__balance-card-amount">
              {walletAmount}
            </WalletBalanceCard.Content.Amount>
          </WalletBalanceCard.Content>
          <WalletBalanceCard.IconWrapper className="wallet__balance-card-icon-wrapper">
            <WalletIcon />
          </WalletBalanceCard.IconWrapper>
        </WalletBalanceCard>
        <div>
          <WalletSectionHeader
            title="Recent Activity"
            actionText="View All"
            className="wallet__transactions-header"
            titleClassName="heading"
            onAction={handleGoToViewAll}
            icon={ChevronRightIcon}
          />
          <WalletTransactionList
            transactions={recentTransactions}
            className="wallet__transparent-transactions-bg"
            renderItem={transaction => (
              <WalletTransaction
                title={transaction?.description}
                date={formatDateTime(transaction?.createdAt)}
                amount={transaction?.amount}
                type={(transaction?.transactionType || '').toLowerCase()}
                showIcon
              />
            )}
          />
        </div>
      </>
    );
  };

  return (
    <div className="wallet">
      <WalletHeader
        title="BK-Wallet"
        showBackButton={isMobileScreen}
        onBack={handleGoBack}
      />
      {walletContent()}
    </div>
  );
};

BKWalletWithoutTabs.propTypes = {
  onNavigate: PropTypes.func.isRequired
};

const BKWallet = ({ theme }) => {
  const [currentScreen, setCurrentScreen] = useState('without-tabs');
  const [height, setHeight] = useState('auto');

  const handleGoToScreen = useCallback(tab => setCurrentScreen(tab), []);

  useLayoutEffect(() => {
    const sideElement = document.querySelector('.account__left-content');

    if (!sideElement) return undefined;

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      setHeight(`${entry.contentRect.height}px`);
    });

    resizeObserver.observe(sideElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      data-theme={theme}
      className="wallet_outside_con"
      style={{ '--card-height': `${height}` }}
    >
      {currentScreen !== 'tabs' ? (
        <BKWalletWithoutTabs onNavigate={handleGoToScreen} />
      ) : (
        <BkWalletWithTabs theme={theme} onNavigate={handleGoToScreen} />
      )}
    </div>
  );
};

export default BKWallet;
